/* eslint-disable @typescript-eslint/unbound-method */
import produce from 'immer';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {EventConstants} from 'constants/EventConstants';
import * as context from 'Context';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import Store from 'lib/Store';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import SubtemplateUtil from 'notebook/entry-templates/SubtemplateUtil';
import VersionedItemSubstore, {ItemEditorState, ItemVersionState} from 'notebook/store/VersionedItemSubstore';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {removeQueryResolutionsForTableFromSnapshot} from 'notebook/tables/excess/Resolver';
import {MultidocTableOpMap} from 'notebook/tables/TableConstants';
import {createNoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
import {BioentityNote, NotePart} from 'notebook/util/NoteUtilConstants';

import SubtemplateUpdater from './SubtemplateUpdater';
import {
  FromServerSubtemplate,
  FromServerSubtemplateVersion,
  Subtemplate,
  TemplateCollection,
  ToServerSubtemplate,
} from './TemplateConstants';

// Store for a collection of Sub-Templates
class SubtemplateStore extends Store {
  itemSubstore!: VersionedItemSubstore<
    Subtemplate,
    NotePart,
    FromServerSubtemplate,
    FromServerSubtemplateVersion,
    'subtemplate',
    'subtemplateId'
  >;
  collectionId: string | null = '';
  collection!: TemplateCollection | null;
  collections: Array<TemplateCollection> | null = [];

  postSanitizeItem(
    id: string,
    newSubtemplate: Subtemplate,
    oldSubtemplate: Subtemplate,
    {
      bumpVersion,
      performTableEvaluation = true,
      multidocTableOp,
    }: {bumpVersion?: boolean; performTableEvaluation?: boolean; multidocTableOp?: MultidocTableOpMap}
  ): Subtemplate {
    // TODO(BNCH-30610): Remove this edge case once persist eval has been fully rolled out (when ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL will always be true)
    if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL) {
      // // If Excess Version is null we can no-op since it signifies an empty NRS
      if (newSubtemplate.subtemplateVersion.noteVersion.noteRuntimeSnapshot.excessVersion == null) {
        return newSubtemplate;
      }
      const emptySnapshot = createNoteRuntimeSnapshot();
      return {
        ...newSubtemplate,
        subtemplateVersion: {
          ...newSubtemplate.subtemplateVersion,
          noteVersion: {
            ...newSubtemplate.subtemplateVersion.noteVersion,
            noteRuntimeSnapshot: emptySnapshot,
          },
        },
      };
    }

    if (!performTableEvaluation) {
      return newSubtemplate;
    }

    // We always set performTableEvaluation=true when we update structured table mappings on the subtemplate,
    // so we can skip updating the table mapping global state if performTableEvaluation=false
    if (
      oldSubtemplate?.subtemplateVersion.noteVersion.structuredTableMappings !==
      newSubtemplate.subtemplateVersion.noteVersion.structuredTableMappings
    ) {
      StructuredTableMappingsGlobalState.updateState(
        id,
        StructuredTableMappingsStateManager.setStructuredTableMappings(
          newSubtemplate.subtemplateVersion.noteVersion.structuredTableMappings ?? []
        )
      );
    }

    const updater = new SubtemplateUpdater(
      id,
      () => this.itemSubstore.getItem(id),
      (subtemplateVersion, asyncMultidocTableOp) => {
        const SubtemplateActions = require('notebook/entry-templates/SubtemplateActions').default;
        SubtemplateActions.update(
          id,
          {$merge: {subtemplateVersion}},
          {bumpVersion, multidocTableOp: asyncMultidocTableOp}
        );
      },
      {multidocTableOp}
    );
    return updater.run(newSubtemplate, oldSubtemplate);
  }

  override resetState(): void {
    this.collections = null; // list of collections (summary dictionaries) that user has access to
    this.collection = null; // active collection (full dictionary)
    this.collectionId = null;
    if (this.itemSubstore == null) {
      this.itemSubstore = new VersionedItemSubstore<
        Subtemplate,
        NotePart,
        FromServerSubtemplate,
        FromServerSubtemplateVersion,
        'subtemplate',
        'subtemplateId'
      >({
        options: {
          idKey: 'subtemplateId',
          itemKey: 'subtemplate',
          getVersionCid(item: Subtemplate) {
            return item.subtemplateVersion.cid;
          },
          asyncItemLoad: true,
          postSanitizeItem: this.postSanitizeItem.bind(this),
          sanitizeItem: SubtemplateUtil.sanitizeSubtemplate,
          prepareForStore: SubtemplateUtil.prepareForStore,
          pickRevertableFields: SubtemplateUtil.pickRevertableFields,
          doSave(id: string) {
            const SubtemplateActions = require('notebook/entry-templates/SubtemplateActions').default;
            return SubtemplateActions.save(id);
          },
        },
        events: {
          item: {
            load: {
              init: EventConstants.SUBTEMPLATE_OPEN,
              completed: EventConstants.SUBTEMPLATE_OPEN_COMPLETED,
              failed: EventConstants.SUBTEMPLATE_OPEN_FAILED,
            },
            registerObjects: EventConstants.SUBTEMPLATE_REGISTER_OBJECTS,
            close: EventConstants.SUBTEMPLATE_CLOSE,
            rename: EventConstants.FILE_BROWSER_RENAME_FILE,
            resetToOpenVersion: EventConstants.SUBTEMPLATE_RESET_TO_OPEN_VERSION,
            revert: EventConstants.SUBTEMPLATE_REVERT,
            update: EventConstants.SUBTEMPLATE_UPDATE,
            save: {
              init: EventConstants.SUBTEMPLATE_SAVE,
              completed: EventConstants.SUBTEMPLATE_SAVE_COMPLETED,
              failed: EventConstants.SUBTEMPLATE_SAVE_FAILED,
            },
          },
          version: {
            load: {
              init: EventConstants.SUBTEMPLATE_LOAD_VERSION,
              completed: EventConstants.SUBTEMPLATE_LOAD_VERSION_COMPLETED,
              failed: EventConstants.SUBTEMPLATE_LOAD_VERSION_FAILED,
            },
          },
          versions: {
            fetch: {
              init: EventConstants.SUBTEMPLATE_FETCH_VERSIONS,
              completed: EventConstants.SUBTEMPLATE_FETCH_VERSIONS_COMPLETED,
              failed: EventConstants.SUBTEMPLATE_FETCH_VERSIONS_FAILED,
            },
          },
        },
      });
    }
    this.itemSubstore.resetState();
  }

  inStore(id: string): boolean {
    return this.itemSubstore.inStore(id);
  }

  getCollections(): Array<TemplateCollection> | null {
    return this.collections;
  }

  getCollection(): TemplateCollection {
    return this.collection!;
  }

  getSubtemplate(): Subtemplate | null {
    const subtemplateIds = Object.keys(this.itemSubstore.items);
    if (subtemplateIds.length !== 1) {
      return null;
    }
    return this.itemSubstore.items[subtemplateIds[0]].subtemplate;
  }

  getNote(): BioentityNote | null {
    const subtemplate = this.getSubtemplate();
    return subtemplate?.subtemplateVersion.noteVersion ?? null;
  }

  getEditorState(id: string): ItemEditorState<Subtemplate, NotePart, 'subtemplate', 'subtemplateId'> {
    return this.itemSubstore.getEditorState(id);
  }

  getVersionState(id: string): ItemVersionState<FromServerSubtemplateVersion> {
    return this.itemSubstore.getVersionState(id);
  }

  getIdFromCid(id: string, cid: string): string | number | undefined {
    return this.itemSubstore.getIdFromCid(id, cid);
  }

  hasUnsavedChanges(id: string): boolean {
    return this.itemSubstore.hasUnsavedChanges(id);
  }

  override payloadHandler(action: FluxAction): boolean {
    // @ts-expect-error ts(2345)
    let shouldEmit = this.itemSubstore.handlePayload(action);
    switch (action.actionType) {
      case EventConstants.SUBTEMPLATE_SAVE_COMPLETED: {
        // propagate any new note part provenance IDs to the store
        const currSubtemplate = this.getSubtemplate();
        const {subtemplateId, cidNotePartProvenanceIdMap} = action;
        if (currSubtemplate == null) {
          break;
        }
        const updatedSubtemplate = produce(currSubtemplate, (draft) => {
          draft.subtemplateVersion.noteVersion.noteParts.forEach((notePart) => {
            if (
              cidNotePartProvenanceIdMap &&
              cidNotePartProvenanceIdMap[notePart.cid] != null &&
              notePart.provenanceId !== cidNotePartProvenanceIdMap[notePart.cid]
            ) {
              notePart.provenanceId = cidNotePartProvenanceIdMap[notePart.cid];
            }
          });
        });

        this.itemSubstore.updateItem(subtemplateId, updatedSubtemplate, {
          bumpVersion: false,
          performTableEvaluation: false,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE: {
        // Possible with subtemplates?
        const currSubtemplate = this.getSubtemplate();
        const {itemId, provenanceUuid, tableOp, lookupConfigIds} = action; // Same or different compared to templates?
        if (currSubtemplate == null) {
          break;
        }
        const updatedSubtemplate = {
          ...currSubtemplate,
          subtemplateVersion: {
            ...currSubtemplate.subtemplateVersion,
            noteVersion: {
              ...currSubtemplate.subtemplateVersion.noteVersion,
              noteRuntimeSnapshot: removeQueryResolutionsForTableFromSnapshot(
                currSubtemplate.subtemplateVersion.noteVersion.noteRuntimeSnapshot,
                provenanceUuid,
                lookupConfigIds
              ),
            },
          },
        };

        this.itemSubstore.updateItem(itemId, updatedSubtemplate, {
          bumpVersion: true,
          multidocTableOp: {[itemId]: tableOp},
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_FORCE_EVALUATION: {
        const {fileId} = action;
        // If the sub-template isn't in the store anymore we can skip evaluation.
        if (!this.inStore(fileId)) {
          return true;
        }

        const {subtemplate} = this.itemSubstore.getItemStateUnsafe(fileId);

        const updater = new SubtemplateUpdater(
          fileId,
          () => this.itemSubstore.getItem(fileId),
          (subtemplateVersion) => {
            const SubtemplateActions = require('notebook/entry-templates/SubtemplateActions').default;
            SubtemplateActions.update(fileId, {$merge: {subtemplateVersion}}, {bumpVersion: false});
          }
        );
        context.using({forceExcessRevaluate: true}, this, () => updater.run(subtemplate, subtemplate));

        shouldEmit = true;
        break;
      }
      case EventConstants.SUBTEMPLATE_OPEN_COMPLETED: {
        const subtemplate = this.getSubtemplate();
        if (subtemplate == null) {
          break;
        }
        this.collectionId = subtemplate.templateCollection.id;
        this.collection = action.templateCollection ?? subtemplate.templateCollection;
        shouldEmit = true;
        break;
      }
      case EventConstants.SUBTEMPLATE_DELETE_COMPLETED:
        _.defer(() => {
          navigateToURL(TemplateCollectionDAO.collectionURL(this.collectionId!));
        });
        shouldEmit = false;
        break;
      case EventConstants.TEMPLATE_COLLECTION_FETCH:
        // If the collection is already loaded, we can keep the existing data.
        if (this.collection != null && this.collectionId === action.collectionId) {
          break;
        }
        this.collectionId = action.collectionId;
        this.collection = null;
        shouldEmit = true;
        break;
      case EventConstants.TEMPLATE_COLLECTION_FETCH_COMPLETED:
        // If a different collection has finished loading than the desired one, ignore it.
        if (this.collectionId !== action.collection.id) {
          break;
        }
        this.collection = action.collection;
        shouldEmit = true;
        break;
      case EventConstants.TEMPLATE_COLLECTION_UPDATE_COMPLETED:
        if (this.collectionId !== action.collectionId) {
          break;
        }
        // eslint-disable-next-line no-restricted-syntax
        this.collection = _.extend({}, this.collection, action.collection);
        shouldEmit = true;
        break;

      case EventConstants.TEMPLATE_COLLECTION_DELETE_COMPLETED:
        if (this.collectionId !== action.collectionId) {
          break;
        }
        this.collection = null;
        this.collectionId = null;

        // If the open template collection is deleted, we should navigate back to the
        // collections page. We defer the navigate as it will trigger another action.
        _.defer(() => {
          navigateToURL(TemplateCollectionDAO.collectionsURL());
        });
        shouldEmit = true;
        break;
      default:
        break;
    }

    if (shouldEmit) {
      this.emitChange();
    }
    return true;
  }

  prepareForSave(id: string, subtemplate: Subtemplate): ToServerSubtemplate {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    return SubtemplateUtil.prepareForSave(subtemplate, cidIdMap);
  }

  __serverBootstrapItem(object: {id: string; item: FromServerSubtemplate}): void {
    this.itemSubstore.__serverBootstrapItem(object);
  }
}

const subtemplateStore = new SubtemplateStore(AppDispatcher);
export default subtemplateStore;
